import type { Identity } from '../types/auth';

/** Permission flag (tesseract-auth) that unlocks the Staff Manager panel. */
export const STAFF_PANEL_PERMISSION = 'argus.staff.read';
/** Tesseract team membership management + user search (implies `argus.staff.read`). */
export const STAFF_MANAGE_PERMISSION = 'argus.staff.manage';
/** Read Staff Manager Notes (staff panel and player page). */
export const STAFF_NOTES_READ_PERMISSION = 'argus.staff.notes.read';
/** Create / edit / delete Staff Manager Notes (implies `argus.staff.notes.read`). */
export const STAFF_NOTES_WRITE_PERMISSION = 'argus.staff.notes.write';

export interface StaffPerms {
  /** Open the Staff Manager: roster, directory, teams (read), member summaries, steward profiles. */
  canView: boolean;
  /** Change Tesseract team membership from ARGUS (Tesseract still requires the actor's own role.edit). */
  canManage: boolean;
  canReadNotes: boolean;
  canWriteNotes: boolean;
}

/**
 * Staff Manager permission matrix. `SPY_Super_admin` bypasses everything like the other panel
 * modules; `argus.staff.manage` implies `argus.staff.read`; `notes.write` implies `notes.read`.
 * Client-side only — the backend enforces the same rules (src/auth.ts in argus-backend) on every
 * request, and Tesseract enforces its own permissions on every membership change.
 */
export const getStaffPerms = (identity: Identity | null, isDev: boolean): StaffPerms => {
  if (isDev) return { canView: true, canManage: true, canReadNotes: true, canWriteNotes: true };
  if (!identity) return { canView: false, canManage: false, canReadNotes: false, canWriteNotes: false };
  const groups: string[] = identity.raw?.custom?.groups || [];
  const permissions: string[] = identity.raw?.custom?.permissions || [];
  const superAdmin = groups.includes('SPY_Super_admin');
  const manage = superAdmin || permissions.includes(STAFF_MANAGE_PERMISSION);
  const notesWrite = superAdmin || permissions.includes(STAFF_NOTES_WRITE_PERMISSION);
  return {
    canView: manage || permissions.includes(STAFF_PANEL_PERMISSION),
    canManage: manage,
    canReadNotes: notesWrite || permissions.includes(STAFF_NOTES_READ_PERMISSION),
    canWriteNotes: notesWrite,
  };
};

/**
 * Staff Manager visibility. Shared by the App.tsx route guard and the sidebar item so the two
 * never drift.
 */
export const canViewStaffPanel = (identity: Identity | null, isDev: boolean): boolean =>
  getStaffPerms(identity, isDev).canView;
